/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import _ from 'lodash';
import dateFormatter from '@/utils/dateFormatter';
import errorFormatter from '@/utils/errorFormatter';
import { loadVideoByKey, updateVideoByKey, deleteVideo } from '@/video/api';
import { validateContentCategory, validateTitle, getErrorMessageFromErrorStatus } from '../../validator/contentValidator';

const state = {
  currentVideo: {},
  loadVideoState: {
    status: 'idle',
    error: null,
  },
  saveVideoState: {
    status: 'idle',
    error: null,
  },
};

const defaultState = {
  currentVideo: {},
  loadVideoState: {
    status: 'idle',
    error: null,
  },
  saveVideoState: {
    status: 'idle',
    error: null,
  },
};

const getters = {
  currentVideo: (state) => state.currentVideo,
};

const mutations = {
  setCurrentVideo(state, videoData) {
    state.currentVideo = { ...state.currentVideo, ...videoData };

    if (state.currentVideo.type !== 'live') {
      const contentCategoryError = validateContentCategory(
        state.currentVideo.contentCategory,
        state.currentVideo.status,
        state.saveVideoState.error,
      );
      state.saveVideoState.error = { ...state.saveVideoState.error, ...contentCategoryError };
    }

    const episode = _.get(state, 'currentVideo.episode', null);
    if (episode) {
      const label = episode.firstAiredAt ? `(${dateFormatter.methods.formatShortDate(episode.firstAiredAt)}) ${episode.title}` : episode.title;
      state.currentVideo.episode.label = label;
    }

    const titleError = validateTitle(state.currentVideo.title);
    state.saveVideoState.error = { ...state.saveVideoState.error, ...titleError };
  },
  setLoadVideoState(state, message) {
    switch (message.type) {
      case 'loading':
        state.loadVideoState.status = 'loading';
        state.loadVideoState.error = null;
        break;
      case 'success':
        state.loadVideoState.status = 'success';
        break;
      case 'error':
        state.loadVideoState.status = 'error';
        state.loadVideoState.error = message.payload;
        break;
      default:
        state.loadVideoState.status = 'idle';
        state.loadVideoState.error = null;
    }
  },
  setSaveVideoState(state, message) {
    switch (message.type) {
      case 'saving':
        state.saveVideoState.status = 'saving';
        state.saveVideoState.error = null;
        break;
      case 'success':
        state.saveVideoState.status = 'saved';
        break;
      case 'error':
        state.saveVideoState.status = 'error';
        state.saveVideoState.error = message.payload;
        break;
      default:
        state.saveVideoState.status = 'idle';
        state.saveVideoState.error = null;
    }
  },
  setVideoAttachmentID(state, attachmentID) {
    state.currentVideo.attachmentId = attachmentID;
  },
  setVideoGalleryID(state, galleryID) {
    state.currentVideo.galleryId = galleryID;
  },
  destroyCurrentVideo(state) {
    Object.assign(state, _.cloneDeep(defaultState));
  },
};

const actions = {
  async loadVideoByVideoId({ dispatch, commit }, videoId) {
    try {
      commit('destroyCurrentVideo');
      dispatch('destroyCurrentGalleryState');
      dispatch('resetAttachment');
      commit('setLoadVideoState', { type: 'loading' });
      const response = await loadVideoByKey(videoId);
      await dispatch('setCurrentVideo', response.data);
      if (response.data.attachmentId) {
        await dispatch('loadAttachment', response.data.attachmentId);
      }
      if (response.data.galleryId) {
        await dispatch('loadCurrentGallery', response.data.galleryId);
      }
      commit('setLoadVideoState', { type: 'success' });
    } catch (error) {
      commit('setLoadVideoState', {
        type: 'error',
        payload: error.response,
      });
    }
  },
  async updateVideoByVideoId({ dispatch, commit }, body) {
    try {
      commit('setSaveVideoState', { type: 'saving' });
      const response = await updateVideoByKey(body.videoId, body.payload);
      await dispatch('setCurrentVideo', response.data);
      commit('setSaveVideoState', { type: 'success' });
    } catch (error) {
      commit('setSaveVideoState', {
        type: 'error',
        payload: {
          ...errorFormatter.methods.transformErrorData(error.response.data),
          ...getErrorMessageFromErrorStatus(error.response.status),
        },
      });
    }
  },
  async deleteVideo({ commit }, videoId) {
    try {
      await deleteVideo(videoId);
      commit('destroyCurrentVideo');
    } catch (error) {
      commit('setSaveVideoState', {
        type: 'error',
        payload: {
          ...errorFormatter.methods.transformErrorData(error.response.data),
          ...getErrorMessageFromErrorStatus(error.response.status),
        },
      });
    }
  },
  setCurrentVideo({ commit }, videoData) {
    commit('setCurrentVideo', videoData);
  },
  setVideoAttachmentID({ commit }, attachmentID) {
    commit('setVideoAttachmentID', attachmentID);
  },
  destroyCurrentVideo({ commit }) {
    commit('destroyCurrentVideo');
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
